import React, { useMemo, useRef, useState } from 'react';
import { buildVisualTemplateFromImage, normalizedPointFromImage } from '../Apps/visualTemplateSelection';
import { formatSequenceStep, profileStepFromToken } from './automationSequenceSteps';

const RECOVERY_ACTIONS = ['input', 'home', 'back', 'up', 'down', 'left', 'right', 'enter'];

export default function VisualCheckpointEditor({ screenshotDataUrl, stepIndex, routinePrefix, recoveryActions = RECOVERY_ACTIONS, onSave, onCancel }) {
  const imageRef = useRef(null);
  const startRef = useRef(null);
  const [selection, setSelection] = useState(null);
  const [threshold, setThreshold] = useState(0.86);
  const [maxAttempts, setMaxAttempts] = useState(2);
  const [recoveryMode, setRecoveryMode] = useState('retry');
  const [customRecovery, setCustomRecovery] = useState([]);
  const [message, setMessage] = useState('Draw a box around stable text, an icon, or another distinctive part of this menu state.');

  const recoverySteps = useMemo(() => {
    const actions = recoveryMode === 'restart' ? routinePrefix : recoveryMode === 'custom' ? customRecovery : [];
    return actions.map((action) => profileStepFromToken(action, 600));
  }, [customRecovery, recoveryMode, routinePrefix]);

  const point = (event) => normalizedPointFromImage(event.clientX, event.clientY, imageRef.current);
  const handlePointerDown = (event) => {
    const next = point(event);
    if (!next) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    startRef.current = next;
    setSelection({ x: next.x, y: next.y, width: 0, height: 0 });
  };
  const handlePointerMove = (event) => {
    if (!startRef.current) return;
    const next = point(event);
    if (!next) return;
    setSelection({
      x: Math.min(startRef.current.x, next.x),
      y: Math.min(startRef.current.y, next.y),
      width: Math.abs(next.x - startRef.current.x),
      height: Math.abs(next.y - startRef.current.y),
    });
  };
  const handlePointerUp = () => { startRef.current = null; };

  const save = () => {
    if (!selection || selection.width < 0.01 || selection.height < 0.01) {
      setMessage('Draw a larger detection region before saving this checkpoint.');
      return;
    }
    try {
      const template = buildVisualTemplateFromImage(imageRef.current, selection, threshold);
      onSave({
        id: `visual-step-${stepIndex + 1}`,
        match: 'any',
        observations: [{
          type: 'screenshot_template',
          templateId: `input-step-${stepIndex + 1}`,
          minSimilarity: Number(threshold),
          template,
        }],
        maxAttempts: Number(maxAttempts),
        retryDelayMs: 900,
        recoverySteps,
      });
    } catch (error) {
      setMessage(error.message || 'The selected crop could not be saved.');
    }
  };

  return (
    <div className="visual-checkpoint-editor">
      <header><div><span>Visual checkpoint</span><h4>Confirm state after command {stepIndex + 1}</h4></div><button type="button" onClick={onCancel}>Cancel</button></header>
      <p>{message}</p>
      <div className="apps-visual-template-selector" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} role="application" aria-label="Draw a visual checkpoint region">
        <img ref={imageRef} src={screenshotDataUrl} alt="Current television state" draggable="false" />
        {selection && <span className="apps-visual-template-selection" style={{ left: `${selection.x * 100}%`, top: `${selection.y * 100}%`, width: `${selection.width * 100}%`, height: `${selection.height * 100}%` }} />}
      </div>
      <div className="visual-checkpoint-editor__settings">
        <label>Match confidence<select value={threshold} onChange={(event) => setThreshold(Number(event.target.value))}><option value={0.78}>Flexible · 78%</option><option value={0.86}>Balanced · 86%</option><option value={0.92}>Strict · 92%</option></select></label>
        <label>Attempts<select value={maxAttempts} onChange={(event) => setMaxAttempts(Number(event.target.value))}><option value={1}>Once</option><option value={2}>Twice</option><option value={3}>Three times</option></select></label>
        <label>When not reached<select value={recoveryMode} onChange={(event) => setRecoveryMode(event.target.value)}><option value="retry">Wait and check again</option><option value="restart">Start routine again</option><option value="custom">Run corrective commands</option></select></label>
      </div>
      {recoveryMode === 'restart' && <p className="visual-checkpoint-editor__recovery">Recovery replays: {routinePrefix.map(formatSequenceStep).join(' → ') || 'No earlier commands'}. Global action and time limits still apply.</p>}
      {recoveryMode === 'custom' && <div className="visual-checkpoint-editor__custom"><div>{recoveryActions.map((action) => <button type="button" key={action} onClick={() => setCustomRecovery((current) => [...current, action])}>+ {action}</button>)}</div><div className="visual-checkpoint-editor__recovery-flow">{customRecovery.length ? customRecovery.map((action, index) => <button type="button" key={`${action}-${index}`} onClick={() => setCustomRecovery((current) => current.filter((_, itemIndex) => itemIndex !== index))}>{action} ×</button>) : <span>No corrective commands yet.</span>}</div></div>}
      <button type="button" className="visual-checkpoint-editor__save" onClick={save}>Save checkpoint</button>
    </div>
  );
}
